import { useState, useEffect, useRef } from 'react';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getAnimation, getCardClass, isCardTransparent, hexToRgba, buildSrcSet, getImageSizes, getCardRadius, getCardPadding, getCardGap, getGridCols, getTextAlign } from '../utils';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';
import SectionBgImage from './SectionBgImage';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
gsap.registerPlugin(ScrollTrigger);

const StarRating = ({ rating, accent, dark, starColor }) => {
    const r = parseInt(rating) || 0;
    if (r < 1 || r > 5) return null;
    const filledColor = starColor || accent || '#facc15';
    return (
        <div className="flex items-center gap-0.5 mt-1">
            {[1,2,3,4,5].map(i => (
                <svg key={i} className="w-3.5 h-3.5" viewBox="0 0 20 20" fill={i <= r ? filledColor : (dark ? 'rgba(255,255,255,0.15)' : '#e5e7eb')}>
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.37 2.448a1 1 0 00-.364 1.118l1.287 3.957c.3.921-.755 1.688-1.54 1.118l-3.37-2.448a1 1 0 00-1.175 0l-3.37 2.448c-.784.57-1.838-.197-1.54-1.118l1.287-3.957a1 1 0 00-.364-1.118L2.063 9.384c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.95-.69l1.286-3.957z"/>
                </svg>
            ))}
        </div>
    );
};

export default function TestimonialsSection({ block, primaryColor, secondaryColor, enableAnimations, cardStyle, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const rawItems = content.items || [];
    const items = rawItems.length > 0 ? rawItems : [
        { name: 'Ana Garcia', role: 'CEO, TechCorp', quote: 'Excelente servicio. Superaron nuestras expectativas en todo sentido.', rating: 5 },
        { name: 'Carlos Lopez', role: 'Director de Marketing', quote: 'La mejor decision que tomamos. Resultados visibles desde el primer mes.', rating: 5 },
        { name: 'Maria Torres', role: 'Emprendedora', quote: 'Profesionalismo y calidad. Los recomiendo al 100%.', rating: 5 },
    ];
    const [current, setCurrent] = useState(0);
    const [videoModalId, setVideoModalId] = useState(null);
    const cardsRef = useRef(null);
    const variant = config.variant || 'spotlight';
    const anim = getAnimation(config, visible);
    const columns = config.columns || 3;
    const colClass = columns === 2 ? 'md:grid-cols-2' : columns === 4 ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-2 lg:grid-cols-3';

    useEffect(() => {
        if (variant !== 'spotlight' || items.length <= 1) return;
        const timer = setInterval(() => setCurrent(prev => (prev + 1) % items.length), 6000);
        return () => clearInterval(timer);
    }, [items.length, variant]);

    useEffect(() => {
        if (variant !== 'cards' || !cardsRef.current) return;
        const ctx = gsap.context(() => {
            gsap.from(cardsRef.current.children, {
                opacity: 0, y: 30, duration: 0.6,
                stagger: 0.1, ease: 'power2.out',
                scrollTrigger: { trigger: cardsRef.current, start: 'top 85%', once: true }
            });
        }, cardsRef);
        return () => ctx.revert();
    }, [variant]);

    // Resolve photos from mediaFolders
    const resolvedItems = items.map(item => {
        let photoUrl = item.image_url || null;
        if (item.image_folder_id && item.image_file_id && mediaFolders[item.image_folder_id]) {
            const found = mediaFolders[item.image_folder_id].find(f => String(f.id) === String(item.image_file_id));
            if (found) photoUrl = found.image_url;
        }
        return { ...item, photoUrl };
    });

    const INITIAL_ITEMS = 24;
    const ITEMS_PER_PAGE = 24;
    const [visibleCount, setVisibleCount] = useState(INITIAL_ITEMS);

    if (resolvedItems.length === 0) return null;

    const card = getCardClass(cardStyle, s.dark);
    const tp = isCardTransparent(cardStyle, s.dark);

    const Avatar = ({item, size = 'w-10 h-10', textSize = 'text-sm'}) => (
        item.photoUrl ? (
            <img src={item.photoUrl} alt={item.name || ''} className={`${size} rounded-full object-cover shrink-0`} loading="lazy" decoding="async"/>
        ) : (
            <div className={`${size} rounded-full flex items-center justify-center text-white ${textSize} font-bold shrink-0`} style={{ backgroundColor: s.accent }}>
                {item.name?.charAt(0) || '?'}
            </div>
        )
    );

    const LoadMoreBtn = () => visibleCount < resolvedItems.length ? (
        <div className="text-center mt-8">
            <button onClick={() => setVisibleCount(c => Math.min(c + ITEMS_PER_PAGE, resolvedItems.length))}
                className="px-6 py-2.5 rounded-xl text-sm font-medium border-2 transition-all hover:shadow-md"
                style={{borderColor: s.accent, color: s.accent}}>
                Ver mas ({resolvedItems.length - visibleCount} restantes)
            </button>
        </div>
    ) : null;

    // -- Cards variant --
    if (variant === 'cards') {
        return (
            <section className={`${s.padClass} relative`} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <SectionBgImage config={config} mediaFolders={mediaFolders} />
                <div ref={ref} className={`relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    {content.title && <SectionHeader tag={content.tag || 'Opiniones'} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                    <div ref={cardsRef} className={`grid grid-cols-1 ${getGridCols(config) || colClass} ${getCardGap(config) || 'gap-6'}`}>
                        {resolvedItems.slice(0, visibleCount).map((item, i) => (
                            <div key={i} className={`${card} ${getCardPadding(config) || 'p-6'} transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                style={{ transitionDelay: visible ? `${150 + Math.min(i, 10) * 100}ms` : '0ms' }}>
                                <svg className="w-8 h-8 mb-4 opacity-20" style={{ color: s.accent }} fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"/>
                                </svg>
                                <p className="text-sm leading-relaxed italic mb-4" style={{ color: s.textSub }}>"{item.quote}"</p>
                                {item.rating && <StarRating rating={item.rating} accent={s.accent} dark={s.dark} starColor={item.accent_color || config.star_color} />}
                                <div className="flex items-center gap-3 mt-auto pt-4" style={{ borderTop: `1px solid ${s.dark ? 'rgba(255,255,255,0.1)' : '#f3f4f6'}` }}>
                                    <Avatar item={item} />
                                    <div>
                                        <p className="font-semibold text-sm" style={{ color: s.text }}>{item.name}</p>
                                        {item.role && <p className="text-xs" style={{ color: s.textSub }}>{item.role}</p>}
                                        {item.badge && <span className="inline-block mt-1 text-[10px] font-semibold px-2 py-0.5 rounded-full text-white" style={{backgroundColor: item.accent_color || s.accent}}>{item.badge}</span>}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <LoadMoreBtn />
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // -- Marquee variant --
    if (variant === 'marquee') {
        const half = Math.ceil(resolvedItems.length / 2);
        const row1Items = resolvedItems.slice(0, half);
        const row2Items = resolvedItems.slice(half);
        const row1Doubled = [...row1Items, ...row1Items];
        const row2Doubled = [...row2Items, ...row2Items];
        const uid = `mq_${block.id || 'tst'}`;
        const baseLoop = Math.max(resolvedItems.length * 5, 30);
        const loopDuration = config.marquee_speed === 'slow' ? baseLoop * 1.6 : config.marquee_speed === 'fast' ? baseLoop * 0.6 : baseLoop;
        const marqueeDir = config.marquee_reverse ? 'reverse' : 'normal';
        return (
            <section className={`${s.padClass} relative overflow-hidden`} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <SectionBgImage config={config} mediaFolders={mediaFolders} />
                <div ref={ref} className={`relative z-10 ${anim}`}>
                    {content.title && (
                        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                            <SectionHeader tag={content.tag || 'Opiniones'} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                        </div>
                    )}
                    <div className="space-y-3 sm:space-y-4 md:space-y-5">
                        {/* Row 1 — scrolls left */}
                        <div className={`relative ${uid}-row`}>
                            <div className={`flex gap-3 sm:gap-4 md:gap-5 ${uid}-scroll-left`}>
                                {row1Doubled.map((item, i) => (
                                    <div key={i} className="rounded-xl border p-3 sm:p-4 md:p-5 w-64 sm:w-72 md:w-80 shrink-0" style={{ borderColor: s.dark ? 'rgba(255,255,255,0.1)' : '#e5e7eb', backgroundColor: s.dark ? 'rgba(255,255,255,0.05)' : '#ffffff' }}>
                                        <p className="text-sm leading-relaxed italic mb-3" style={{ color: s.textSub }}>"{item.quote}"</p>
                                        {item.rating && <StarRating rating={item.rating} accent={s.accent} dark={s.dark} starColor={item.accent_color || config.star_color} />}
                                        <div className="flex items-center gap-3 mt-2">
                                            <Avatar item={item} size="w-8 h-8" textSize="text-xs" />
                                            <div>
                                                <p className="font-semibold text-xs" style={{ color: s.text }}>{item.name}</p>
                                                {item.role && <p className="text-[10px]" style={{ color: s.textSub }}>{item.role}</p>}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        {/* Row 2 — scrolls right (reverse) */}
                        {row2Doubled.length > 0 && (
                            <div className={`relative ${uid}-row hidden sm:block`}>
                                <div className={`flex gap-3 sm:gap-4 md:gap-5 ${uid}-scroll-right`}>
                                    {row2Doubled.map((item, i) => (
                                        <div key={i} className="rounded-xl border p-3 sm:p-4 md:p-5 w-64 sm:w-72 md:w-80 shrink-0" style={{ borderColor: s.dark ? 'rgba(255,255,255,0.1)' : '#e5e7eb', backgroundColor: s.dark ? 'rgba(255,255,255,0.05)' : '#ffffff' }}>
                                            <p className="text-sm leading-relaxed italic mb-3" style={{ color: s.textSub }}>"{item.quote}"</p>
                                            {item.rating && <StarRating rating={item.rating} accent={s.accent} dark={s.dark} starColor={item.accent_color || config.star_color} />}
                                            <div className="flex items-center gap-3 mt-2">
                                                <Avatar item={item} size="w-8 h-8" textSize="text-xs" />
                                                <div>
                                                    <p className="font-semibold text-xs" style={{ color: s.text }}>{item.name}</p>
                                                    {item.role && <p className="text-[10px]" style={{ color: s.textSub }}>{item.role}</p>}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
                <style>{`
                    @keyframes ${uid}_left { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
                    @keyframes ${uid}_right { 0% { transform: translateX(-50%); } 100% { transform: translateX(0); } }
                    .${uid}-scroll-left { animation: ${uid}_left ${loopDuration}s linear infinite; animation-direction: ${marqueeDir}; }
                    .${uid}-scroll-right { animation: ${uid}_right ${loopDuration}s linear infinite; animation-direction: ${marqueeDir}; }
                    .${uid}-row:hover .${uid}-scroll-left,
                    .${uid}-row:hover .${uid}-scroll-right { animation-play-state: paused; }
                    .${uid}-row { -webkit-mask-image: linear-gradient(to right, transparent, black 5%, black 95%, transparent); mask-image: linear-gradient(to right, transparent, black 5%, black 95%, transparent); }
                    @media(max-width:639px) {
                        .${uid}-scroll-left { animation-duration: ${loopDuration * 1.5}s; }
                        .${uid}-scroll-right { animation-duration: ${loopDuration * 1.5}s; }
                    }
                `}</style>
            </section>
        );
    }

    // -- Bubbles variant --
    if (variant === 'bubbles') {
        return (
            <section className={`${s.padClass} relative`} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <SectionBgImage config={config} mediaFolders={mediaFolders} />
                <div ref={ref} className={`relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    {content.title && <SectionHeader tag={content.tag || 'Opiniones'} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                    <div className="space-y-6">
                        {resolvedItems.slice(0, visibleCount).map((item, i) => (
                            <div key={i} className={`flex gap-4 ${i % 2 === 1 ? 'flex-row-reverse' : ''} transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                style={{ transitionDelay: visible ? `${Math.min(i, 10) * 150}ms` : '0ms' }}>
                                <Avatar item={item} />
                                <div className={`max-w-[75%] ${i % 2 === 1 ? 'text-right' : ''}`}>
                                    <div className="rounded-2xl px-5 py-4 shadow-sm" style={{ backgroundColor: i % 2 === 0 ? hexToRgba(s.accent, 0.03) : (s.dark ? 'rgba(255,255,255,0.05)' : '#f9fafb') }}>
                                        <p className="text-sm leading-relaxed" style={{ color: s.textSub }}>"{item.quote}"</p>
                                        {item.rating && <div className={i % 2 === 1 ? 'flex justify-end' : ''}><StarRating rating={item.rating} accent={s.accent} dark={s.dark} starColor={item.accent_color || config.star_color} /></div>}
                                    </div>
                                    <p className="text-xs font-semibold mt-2" style={{ color: s.text }}>{item.name}{item.role ? ` · ${item.role}` : ''}{item.badge && <span className="ml-2 inline-block text-[9px] font-semibold px-2 py-0.5 rounded-full text-white" style={{backgroundColor: s.accent}}>{item.badge}</span>}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                    <LoadMoreBtn />
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // -- Video variant --
    if (variant === 'video') {
        const extractYouTubeId = (url) => {
            if (!url) return null;
            const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&?\s]+)/);
            return m ? m[1] : null;
        };
        const VideoCard = ({ item, index }) => {
            const ytId = extractYouTubeId(item.video_url);
            return (
                <div className={`${card} overflow-hidden transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                    style={{ transitionDelay: visible ? `${150 + index * 100}ms` : '0ms' }}>
                    {ytId ? (
                        <div className="relative aspect-video bg-black cursor-pointer group" onClick={() => setVideoModalId(ytId)}>
                            <img src={`https://img.youtube.com/vi/${ytId}/hqdefault.jpg`} alt={item.name || ''} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" decoding="async"/>
                            <div className="absolute inset-0 bg-black/10 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center">
                                <div className="w-14 h-14 rounded-full flex items-center justify-center text-white transition-all duration-300 group-hover:scale-110 group-hover:shadow-2xl"
                                    style={{ backgroundColor: s.accent, boxShadow: `0 0 0 6px ${hexToRgba(s.accent, 0.15)}` }}>
                                    <svg className="w-6 h-6 ml-1" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="p-6 pb-0">
                            <svg className="w-8 h-8 mb-4 opacity-20" style={{ color: s.accent }} fill="currentColor" viewBox="0 0 24 24">
                                <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"/>
                            </svg>
                        </div>
                    )}
                    <div className="p-6">
                        <p className="text-sm leading-relaxed italic mb-4" style={{ color: s.textSub }}>"{item.quote}"</p>
                        {item.rating && <StarRating rating={item.rating} accent={s.accent} dark={s.dark} starColor={item.accent_color || config.star_color} />}
                        <div className="flex items-center gap-3 mt-auto pt-4" style={{ borderTop: `1px solid ${s.dark ? 'rgba(255,255,255,0.1)' : '#f3f4f6'}` }}>
                            <Avatar item={item} />
                            <div>
                                <p className="font-semibold text-sm" style={{ color: s.text }}>{item.name}</p>
                                {item.role && <p className="text-xs" style={{ color: s.textSub }}>{item.role}</p>}
                                {item.badge && <span className="inline-block mt-1 text-[10px] font-semibold px-2 py-0.5 rounded-full text-white" style={{backgroundColor: s.accent}}>{item.badge}</span>}
                            </div>
                        </div>
                    </div>
                </div>
            );
        };
        return (
            <section className={`${s.padClass} relative`} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <SectionBgImage config={config} mediaFolders={mediaFolders} />
                <div ref={ref} className={`relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    {content.title && <SectionHeader tag={content.tag || 'Opiniones'} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                    <div className={`grid grid-cols-1 ${colClass} gap-6`}>
                        {resolvedItems.map((item, i) => (
                            <VideoCard key={i} item={item} index={i} />
                        ))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
                {/* Video modal */}
                {videoModalId && (
                    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 p-4 md:p-8"
                        onClick={() => setVideoModalId(null)}>
                        <div className="relative w-full max-w-5xl" onClick={e => e.stopPropagation()}>
                            <button onClick={() => setVideoModalId(null)}
                                className="absolute -top-12 right-0 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors z-10">
                                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/></svg>
                            </button>
                            <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-video">
                                <iframe src={`https://www.youtube.com/embed/${videoModalId}?autoplay=1&rel=0&modestbranding=1`}
                                    className="absolute inset-0 w-full h-full" style={{ border: 'none' }}
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                            </div>
                        </div>
                    </div>
                )}
            </section>
        );
    }

    // -- Minimal variant --
    if (variant === 'minimal') {
        return (
            <section className={`${s.padClass} relative`} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <SectionBgImage config={config} mediaFolders={mediaFolders} />
                <div ref={ref} className={`relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    {content.title && <SectionHeader tag={content.tag || 'Opiniones'} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                    <div className={`grid grid-cols-1 ${colClass} gap-x-12 gap-y-8`}>
                        {resolvedItems.map((item, i) => (
                            <div key={i} className={`border-l-2 pl-5 transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                style={{ borderColor: hexToRgba(s.accent, 0.25), transitionDelay: visible ? `${i * 100}ms` : '0ms' }}>
                                <p className="text-sm leading-relaxed italic" style={{ color: s.textSub }}>"{item.quote}"</p>
                                {item.rating && <StarRating rating={item.rating} accent={s.accent} dark={s.dark} starColor={item.accent_color || config.star_color} />}
                                <p className="text-xs font-semibold mt-3" style={{ color: s.text }}>-- {item.name}{item.role ? `, ${item.role}` : ''}{item.badge && <span className="ml-2 inline-block text-[9px] font-semibold px-2 py-0.5 rounded-full text-white" style={{backgroundColor: s.accent}}>{item.badge}</span>}</p>
                            </div>
                        ))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // -- Glass variant --
    if (variant === 'glass') {
        return (
            <section className={`${s.padClass} relative`} style={{ background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})` }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <SectionBgImage config={config} mediaFolders={mediaFolders} />
                <div ref={ref} className={`relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    {content.title && <SectionHeader tag={content.tag || 'Opiniones'} title={content.title} subtitle={content.subtitle} config={{ ...config, text_color: '#ffffff' }} s={{ ...s, text: '#ffffff', textSub: 'rgba(255,255,255,0.8)', accent: '#ffffff' }} />}
                    <div className={`grid grid-cols-1 ${colClass} gap-6`}>
                        {resolvedItems.slice(0, visibleCount).map((item, i) => (
                            <div key={i} className={`backdrop-blur-xl bg-white/10 border border-white/20 rounded-2xl p-6 transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                style={{ transitionDelay: visible ? `${150 + Math.min(i, 10) * 100}ms` : '0ms' }}>
                                <p className="text-sm leading-relaxed italic mb-4 text-white">"{item.quote}"</p>
                                {item.rating && <StarRating rating={item.rating} accent="#ffffff" dark={true} starColor={config.star_color || '#facc15'} />}
                                <div className="flex items-center gap-3 mt-auto pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.15)' }}>
                                    <Avatar item={item} />
                                    <div>
                                        <p className="font-semibold text-sm text-white">{item.name}</p>
                                        {item.role && <p className="text-xs text-white/70">{item.role}</p>}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <LoadMoreBtn />
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // -- Masonry variant --
    if (variant === 'masonry') {
        return (
            <section className={`${s.padClass} relative`} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
                <SectionBgImage config={config} mediaFolders={mediaFolders} />
                <div ref={ref} className={`relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    {content.title && <SectionHeader tag={content.tag || 'Opiniones'} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                    <div className={`columns-1 sm:columns-2 lg:columns-3 ${getCardGap(config) || 'gap-4'}`}>
                        {resolvedItems.slice(0, visibleCount).map((item, i) => (
                            <div key={i} className={`break-inside-avoid mb-4 ${getCardRadius(config) || 'rounded-xl'} border ${getCardPadding(config) || 'p-5'} transition-all duration-500 hover:-translate-y-1 hover:shadow-lg ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                style={{ borderColor: s.dark ? 'rgba(255,255,255,0.1)' : '#e5e7eb', backgroundColor: s.dark ? 'rgba(255,255,255,0.05)' : '#ffffff', transitionDelay: visible ? `${Math.min(i, 10) * 80}ms` : '0ms' }}>
                                <p className="text-sm leading-relaxed italic mb-3" style={{ color: s.textSub }}>"{item.quote}"</p>
                                {item.rating && <StarRating rating={item.rating} accent={s.accent} dark={s.dark} starColor={item.accent_color || config.star_color} />}
                                <div className="flex items-center gap-3 mt-3">
                                    <Avatar item={item} size="w-8 h-8" textSize="text-xs" />
                                    <div>
                                        <p className="font-semibold text-xs" style={{ color: s.text }}>{item.name}</p>
                                        {item.role && <p className="text-[10px]" style={{ color: s.textSub }}>{item.role}</p>}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <LoadMoreBtn />
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
            </section>
        );
    }

    // -- Stack variant --
    if (variant === 'stack') {
        const total = resolvedItems.length;
        const prev = (current - 1 + total) % total;
        const next = (current + 1) % total;
        const goNext = () => setCurrent(c => (c + 1) % total);
        const goPrev = () => setCurrent(c => (c - 1 + total) % total);
        const getCardStyle = (i) => {
            if (i === current) return { transform: 'translateX(0) rotate(0deg) scale(1)', opacity: 1, zIndex: 3 };
            if (i === next) return { transform: 'translateX(60px) rotate(3deg) scale(0.95)', opacity: 0.7, zIndex: 2 };
            if (i === prev) return { transform: 'translateX(-60px) rotate(-3deg) scale(0.95)', opacity: 0.7, zIndex: 2 };
            return { transform: 'translateX(0) rotate(0deg) scale(0.9)', opacity: 0, zIndex: 1 };
        };
        return (
            <section className={`${s.padClass} relative`} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <SectionBgImage config={config} mediaFolders={mediaFolders} />
                <div ref={ref} className={`relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    {content.title && <SectionHeader tag={content.tag || 'Opiniones'} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                    <div className="relative flex items-center justify-center" style={{ minHeight: 340 }}>
                        {resolvedItems.map((item, i) => (
                            <div key={i} className="absolute w-full max-w-md mx-auto cursor-pointer"
                                style={{ ...getCardStyle(i), transition: 'all 0.5s cubic-bezier(.4,0,.2,1)' }}
                                onClick={() => { if (i === next) goNext(); else if (i === prev) goPrev(); }}>
                                <div className={`${card} p-8 relative`}>
                                    {/* Large decorative quote */}
                                    <span className="absolute top-4 left-5 text-6xl font-serif leading-none opacity-15 select-none" style={{ color: s.accent }}>"</span>
                                    <p className="text-base leading-relaxed italic mb-6 mt-6 relative z-10" style={{ color: s.textSub }}>"{item.quote}"</p>
                                    {item.rating && <StarRating rating={item.rating} accent={s.accent} dark={s.dark} starColor={item.accent_color || config.star_color} />}
                                    <div className="flex items-center gap-3 mt-4 pt-4" style={{ borderTop: `1px solid ${s.dark ? 'rgba(255,255,255,0.1)' : '#f3f4f6'}` }}>
                                        <Avatar item={item} size="w-10 h-10" />
                                        <div>
                                            <p className="font-semibold text-sm" style={{ color: s.text }}>{item.name}</p>
                                            {item.role && <p className="text-xs" style={{ color: s.textSub }}>{item.role}</p>}
                                            {item.company && <p className="text-xs" style={{ color: s.textSub }}>{item.company}</p>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    {total > 1 && (
                        <div className="flex justify-center gap-4 mt-8 items-center">
                            <button onClick={goPrev} className="w-9 h-9 rounded-full border flex items-center justify-center transition-colors hover:bg-black/5" style={{ borderColor: s.dark ? 'rgba(255,255,255,0.2)' : '#e5e7eb', color: s.text }}>
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7"/></svg>
                            </button>
                            <div className="flex gap-1.5">
                                {resolvedItems.map((_, i) => (
                                    <button key={i} onClick={() => setCurrent(i)}
                                        className={`h-2 rounded-full transition-all duration-300 ${i === current ? 'w-7' : 'w-2'}`}
                                        style={{ backgroundColor: i === current ? s.accent : hexToRgba(s.accent, 0.2) }} />
                                ))}
                            </div>
                            <button onClick={goNext} className="w-9 h-9 rounded-full border flex items-center justify-center transition-colors hover:bg-black/5" style={{ borderColor: s.dark ? 'rgba(255,255,255,0.2)' : '#e5e7eb', color: s.text }}>
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7"/></svg>
                            </button>
                        </div>
                    )}
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // -- Glassmorphism variant --
    if (variant === 'glassmorphism') {
        const gmColClass = resolvedItems.length === 1 ? 'max-w-lg mx-auto' : resolvedItems.length === 2 ? 'grid grid-cols-1 md:grid-cols-2 gap-6' : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6';
        return (
            <section className={`${s.padClass} relative`} style={{ backgroundColor: '#0f172a' }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <SectionBgImage config={config} mediaFolders={mediaFolders} />
                <div ref={ref} className={`relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    {content.title && <SectionHeader tag={content.tag || 'Opiniones'} title={content.title} subtitle={content.subtitle} config={{ ...config, text_color: '#ffffff' }} s={{ ...s, text: '#ffffff', textSub: 'rgba(255,255,255,0.6)', accent: s.accent }} />}
                    <div className={gmColClass}>
                        {resolvedItems.slice(0, visibleCount).map((item, i) => (
                            <div key={i} className={`bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 transition-all duration-500 hover:bg-white/10 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                style={{ transitionDelay: visible ? `${150 + Math.min(i, 10) * 100}ms` : '0ms' }}>
                                {/* Large decorative quote */}
                                <span className="block text-5xl font-serif leading-none mb-2 select-none" style={{ color: hexToRgba(s.accent, 0.35) }}>"</span>
                                <p className="text-sm leading-relaxed text-white mb-5">"{item.quote}"</p>
                                {item.rating && <StarRating rating={item.rating} accent={s.accent} dark={true} starColor={config.star_color || '#facc15'} />}
                                <div className="flex items-center gap-3 mt-4 pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                                    {item.photoUrl ? (
                                        <img src={item.photoUrl} alt={item.name || ''} className="w-10 h-10 rounded-full object-cover shrink-0 ring-2" style={{ ringColor: s.accent, boxShadow: `0 0 0 2px ${s.accent}` }} loading="lazy" decoding="async"/>
                                    ) : (
                                        <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0 ring-2" style={{ backgroundColor: hexToRgba(s.accent, 0.3), boxShadow: `0 0 0 2px ${s.accent}` }}>
                                            {item.name?.charAt(0) || '?'}
                                        </div>
                                    )}
                                    <div>
                                        <p className="font-semibold text-sm text-white">{item.name}</p>
                                        {item.role && <p className="text-xs" style={{ color: 'rgba(255,255,255,0.6)' }}>{item.role}</p>}
                                        {item.company && <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>{item.company}</p>}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <LoadMoreBtn />
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // -- Tweet Wall variant --
    if (variant === 'tweet-wall') {
        return (
            <section className={`${s.padClass} relative`} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <SectionBgImage config={config} mediaFolders={mediaFolders} />
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className="mt-8 columns-1 sm:columns-2 lg:columns-3 gap-4">
                        {resolvedItems.slice(0, visibleCount).map((item, i) => (
                            <div key={i} className={`mb-4 break-inside-avoid rounded-2xl p-4 border ${s.dark ? 'bg-white/5 border-white/10' : 'bg-white border-gray-100 shadow-sm'}`}>
                                <div className="flex items-center gap-3 mb-3">
                                    {item.avatar_url ? <img src={item.avatar_url} alt="" className="w-10 h-10 rounded-full object-cover" decoding="async"/> : <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white" style={{backgroundColor: s.accent}}>{(item.name||'?')[0]}</div>}
                                    <div className="flex-1 min-w-0">
                                        <p className={`text-sm font-bold truncate ${s.dark ? 'text-white' : 'text-gray-900'}`}>{item.name}</p>
                                        <p className="text-xs truncate" style={{color: s.textSub}}>{item.role || '@' + (item.name||'').toLowerCase().replace(/\s/g,'')}</p>
                                    </div>
                                    <svg className="w-5 h-5 shrink-0 text-[#1DA1F2]" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                                </div>
                                <p className={`text-sm leading-relaxed ${s.dark ? 'text-white/80' : 'text-gray-700'}`}>{item.quote}</p>
                                <div className="flex items-center gap-4 mt-3 text-xs" style={{color: s.textSub}}>
                                    <span>❤️ {Math.floor(Math.random() * 500) + 10}</span>
                                    <span>🔁 {Math.floor(Math.random() * 100) + 5}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                    <LoadMoreBtn />
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // -- Before/After Story variant --
    if (variant === 'before-after') {
        return (
            <section className={`${s.padClass} relative`} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <SectionBgImage config={config} mediaFolders={mediaFolders} />
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className={`mt-8 grid grid-cols-1 ${resolvedItems.length >= 2 ? 'md:grid-cols-2' : ''} gap-6`}>
                        {resolvedItems.slice(0, visibleCount).map((item, i) => (
                            <div key={i} className={`rounded-2xl overflow-hidden border ${s.dark ? 'border-white/10' : 'border-gray-200'}`}>
                                <div className="p-5 bg-gray-900 text-white">
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-red-400">Antes</span>
                                    <p className="text-sm mt-2 text-white/70">{item.before || item.quote?.split('.')[0] || 'El problema...'}</p>
                                </div>
                                <div className={`p-5 ${s.dark ? 'bg-white/5' : 'bg-white'}`}>
                                    <span className="text-[10px] font-bold uppercase tracking-wider" style={{color: s.accent}}>Despues</span>
                                    <p className={`text-sm mt-2 ${s.dark ? 'text-white/80' : 'text-gray-700'}`}>{item.after || item.quote?.split('.').slice(1).join('.') || item.quote}</p>
                                    {item.metric && <div className="mt-3 inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold text-white" style={{backgroundColor: s.accent}}>{item.metric}</div>}
                                </div>
                                <div className={`flex items-center gap-3 p-4 border-t ${s.dark ? 'border-white/10' : 'border-gray-100'}`}>
                                    {item.avatar_url ? <img src={item.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover" decoding="async"/> : <div className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{backgroundColor: s.accent}}>{(item.name||'?')[0]}</div>}
                                    <div><p className={`text-sm font-semibold ${s.dark ? 'text-white' : 'text-gray-900'}`}>{item.name}</p><p className="text-xs" style={{color: s.textSub}}>{item.role}</p></div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <LoadMoreBtn />
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // -- Full Profile (magazine) variant --
    if (variant === 'full-profile') {
        const main = resolvedItems[0];
        const rest = resolvedItems.slice(1, visibleCount);
        if (!main) return null;
        return (
            <section className={`${s.padClass} relative`} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <SectionBgImage config={config} mediaFolders={mediaFolders} />
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />
                    <div className="mt-8 grid md:grid-cols-[2fr_3fr] gap-8">
                        <div className="relative rounded-2xl overflow-hidden min-h-[300px]">
                            {main.avatar_url ? <img src={main.avatar_url} alt={main.name} className="w-full h-full object-cover absolute inset-0" decoding="async"/> : <div className="w-full h-full flex items-center justify-center text-6xl font-bold text-white" style={{backgroundColor: s.accent}}>{(main.name||'?')[0]}</div>}
                            <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                                <p className="text-white font-bold">{main.name}</p>
                                <p className="text-white/70 text-sm">{main.role}</p>
                            </div>
                        </div>
                        <div className="flex flex-col justify-center">
                            <span className="text-5xl font-serif leading-none mb-4" style={{color: s.accent}}>"</span>
                            <blockquote className={`text-xl sm:text-2xl font-light leading-relaxed -mt-6 ${s.dark ? 'text-white' : 'text-gray-800'}`}>{main.quote}</blockquote>
                            {main.metric && <p className="mt-4 text-sm font-bold" style={{color: s.accent}}>{main.metric}</p>}
                            <StarRating rating={main.rating} accent={s.accent} dark={s.dark} />
                        </div>
                    </div>
                    {rest.length > 0 && (
                        <div className={`mt-8 grid grid-cols-1 ${rest.length >= 3 ? 'sm:grid-cols-3' : rest.length === 2 ? 'sm:grid-cols-2' : ''} gap-4`}>
                            {rest.map((item, i) => (
                                <div key={i} className={`rounded-xl p-4 ${s.dark ? 'bg-white/5' : 'bg-gray-50'}`}>
                                    <p className={`text-sm italic ${s.dark ? 'text-white/70' : 'text-gray-600'}`}>"{item.quote}"</p>
                                    <div className="flex items-center gap-2 mt-3">
                                        <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white" style={{backgroundColor: s.accent}}>{(item.name||'?')[0]}</div>
                                        <span className={`text-xs font-medium ${s.dark ? 'text-white' : 'text-gray-800'}`}>{item.name}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    <LoadMoreBtn />
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // -- Rotating Spotlight variant --
    if (variant === 'rotating-spotlight') {
        const [activeIdx, setActiveIdx] = useState(0);
        useEffect(() => {
            if (items.length <= 1) return;
            const timer = setInterval(() => setActiveIdx(i => (i + 1) % items.length), 6000);
            return () => clearInterval(timer);
        }, [items.length]);
        const active = items[activeIdx] || items[0];
        return (
            <section className={`${s.padClass} relative`} style={{...(s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }), minHeight: '60vh'}}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <SectionBgImage config={config} mediaFolders={mediaFolders} />
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center justify-center text-center ${anim}`} style={{minHeight: '50vh'}}>
                    {/* Progress bars */}
                    <div className="flex gap-2 mb-8 w-full max-w-md">
                        {items.map((_, i) => (
                            <div key={i} className={`h-1 flex-1 rounded-full overflow-hidden ${s.dark ? 'bg-white/10' : 'bg-gray-200'}`}>
                                <div className={`h-full rounded-full transition-all ${i === activeIdx ? 'duration-[6000ms]' : 'duration-300'}`}
                                    style={{width: i === activeIdx ? '100%' : i < activeIdx ? '100%' : '0%', backgroundColor: s.accent}}/>
                            </div>
                        ))}
                    </div>
                    <blockquote key={activeIdx} className={`text-2xl sm:text-3xl lg:text-4xl font-light leading-relaxed max-w-3xl animate-[fadeIn_0.5s_ease] ${s.dark ? 'text-white' : 'text-gray-800'}`}>
                        "{active.quote}"
                    </blockquote>
                    <div className="flex items-center gap-3 mt-8">
                        {active.avatar_url ? <img src={active.avatar_url} alt="" className="w-14 h-14 rounded-full object-cover border-2" style={{borderColor: s.accent}} decoding="async"/> : <div className="w-14 h-14 rounded-full flex items-center justify-center text-lg font-bold text-white" style={{backgroundColor: s.accent}}>{(active.name||'?')[0]}</div>}
                        <div className="text-left">
                            <p className={`font-bold ${s.dark ? 'text-white' : 'text-gray-900'}`}>{active.name}</p>
                            <p className="text-sm" style={{color: s.textSub}}>{active.role}</p>
                        </div>
                    </div>
                    <StarRating rating={active.rating} accent={s.accent} dark={s.dark} />
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // -- Metrics variant --
    if (variant === 'metrics') {
        return (
            <section className={`${s.padClass} relative`} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <SectionBgImage config={config} mediaFolders={mediaFolders} />
                <div ref={ref} className={`relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    {content.title && <SectionHeader tag={content.tag || 'Opiniones'} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                    <div className={`grid grid-cols-1 ${getGridCols(config) || 'md:grid-cols-2'} ${getCardGap(config) || 'gap-6'}`}>
                        {resolvedItems.slice(0, visibleCount).map((item, i) => (
                            <div key={i} className={`flex ${getCardRadius(config) || 'rounded-xl'} border transition-all duration-500 hover:-translate-y-1 hover:shadow-lg ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                                style={{ borderColor: s.dark ? 'rgba(255,255,255,0.1)' : '#e5e7eb', backgroundColor: s.dark ? 'rgba(255,255,255,0.03)' : '#ffffff', transitionDelay: visible ? `${Math.min(i, 10) * 100}ms` : '0ms' }}>
                                {/* Left: metric or quote icon */}
                                <div className="flex flex-col items-center justify-center px-5 py-6 shrink-0" style={{ width: '30%', borderRight: `1px solid ${s.dark ? 'rgba(255,255,255,0.08)' : '#f3f4f6'}` }}>
                                    {item.metric ? (
                                        <>
                                            <span className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-none" style={{ color: s.accent }}>{item.metric}</span>
                                            {item.metric_label && <span className="text-[11px] mt-2 text-center font-medium" style={{ color: s.textSub }}>{item.metric_label}</span>}
                                        </>
                                    ) : (
                                        <svg className="w-10 h-10 opacity-20" style={{ color: s.accent }} fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"/>
                                        </svg>
                                    )}
                                </div>
                                {/* Right: quote + author */}
                                <div className="flex flex-col justify-between p-5 flex-1 min-w-0" style={{ width: '70%' }}>
                                    <p className="text-sm leading-relaxed italic mb-3" style={{ color: s.textSub }}>"{item.quote}"</p>
                                    {item.rating && <StarRating rating={item.rating} accent={s.accent} dark={s.dark} starColor={item.accent_color || config.star_color} />}
                                    <div className="flex items-center gap-3 mt-auto pt-3" style={{ borderTop: `1px solid ${s.dark ? 'rgba(255,255,255,0.08)' : '#f3f4f6'}` }}>
                                        <Avatar item={item} size="w-8 h-8" textSize="text-xs" />
                                        <div className="min-w-0">
                                            <p className="font-semibold text-sm truncate" style={{ color: s.text }}>{item.name}</p>
                                            {item.role && <p className="text-xs truncate" style={{ color: s.textSub }}>{item.role}</p>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <LoadMoreBtn />
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // -- Wall variant --
    if (variant === 'wall') {
        const wallItems = resolvedItems.slice(0, 24);
        return (
            <section className={`${s.padClass} relative`} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <SectionBgImage config={config} mediaFolders={mediaFolders} />
                <div ref={ref} className={`relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    {content.title && <SectionHeader tag={content.tag || 'Opiniones'} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                    <div className={`grid ${getGridCols(config) || 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4'} ${getCardGap(config) || 'gap-3'}`}>
                        {wallItems.map((item, i) => (
                            <div key={i} className={`${getCardRadius(config) || 'rounded-lg'} border ${getCardPadding(config) || 'p-3'} transition-colors duration-300 ${visible ? 'opacity-100' : 'opacity-0'}`}
                                style={{
                                    borderColor: s.dark ? 'rgba(255,255,255,0.08)' : '#e5e7eb',
                                    backgroundColor: s.dark ? 'transparent' : 'transparent',
                                    transitionDelay: visible ? `${Math.min(i, 16) * 40}ms` : '0ms',
                                }}
                                onMouseEnter={e => { e.currentTarget.style.backgroundColor = s.dark ? 'rgba(255,255,255,0.05)' : '#f9fafb'; }}
                                onMouseLeave={e => { e.currentTarget.style.backgroundColor = 'transparent'; }}>
                                <div className="flex items-center gap-2 mb-2">
                                    <Avatar item={item} size="w-7 h-7" textSize="text-[10px]" />
                                    <span className="font-semibold text-xs truncate" style={{ color: s.text }}>{item.name}</span>
                                </div>
                                <p className="text-[13px] leading-snug" style={{ color: s.textSub, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                    "{item.quote}"
                                </p>
                                {item.rating && <StarRating rating={item.rating} accent={s.accent} dark={s.dark} starColor={item.accent_color || config.star_color} />}
                            </div>
                        ))}
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // -- Carousel Tiles variant --
    if (variant === 'carousel-tiles') {
        const [featuredIdx, setFeaturedIdx] = useState(0);
        const [fading, setFading] = useState(false);

        useEffect(() => {
            if (resolvedItems.length <= 1) return;
            const timer = setInterval(() => {
                setFading(true);
                setTimeout(() => {
                    setFeaturedIdx(prev => (prev + 1) % resolvedItems.length);
                    setFading(false);
                }, 300);
            }, 5000);
            return () => clearInterval(timer);
        }, [resolvedItems.length]);

        const selectFeatured = (idx) => {
            if (idx === featuredIdx) return;
            setFading(true);
            setTimeout(() => {
                setFeaturedIdx(idx);
                setFading(false);
            }, 300);
        };

        const featured = resolvedItems[featuredIdx] || resolvedItems[0];
        const previewItems = [];
        for (let n = 1; n <= 4; n++) {
            const idx = (featuredIdx + n) % resolvedItems.length;
            if (idx !== featuredIdx) previewItems.push({ item: resolvedItems[idx], idx });
        }

        return (
            <section className={`${s.padClass} relative`} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <SectionBgImage config={config} mediaFolders={mediaFolders} />
                <div ref={ref} className={`relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                    {content.title && <SectionHeader tag={content.tag || 'Opiniones'} title={content.title} subtitle={content.subtitle} config={config} s={s} />}
                    <div className="flex flex-col lg:flex-row gap-6">
                        {/* Featured testimonial */}
                        <div className="lg:w-[42%] shrink-0">
                            <div className={`${card} p-6 sm:p-8 h-full flex flex-col justify-between transition-opacity duration-300 ${fading ? 'opacity-0' : 'opacity-100'}`}>
                                <div>
                                    <svg className="w-10 h-10 mb-4 opacity-20" style={{ color: s.accent }} fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"/>
                                    </svg>
                                    <p className="text-base sm:text-lg leading-relaxed italic mb-6" style={{ color: s.textSub }}>"{featured.quote}"</p>
                                    {featured.rating && <StarRating rating={featured.rating} accent={s.accent} dark={s.dark} starColor={featured.accent_color || config.star_color} />}
                                </div>
                                <div className="flex items-center gap-3 mt-6 pt-4" style={{ borderTop: `1px solid ${s.dark ? 'rgba(255,255,255,0.1)' : '#f3f4f6'}` }}>
                                    <Avatar item={featured} size="w-12 h-12" textSize="text-base" />
                                    <div>
                                        <p className="font-semibold text-sm" style={{ color: s.text }}>{featured.name}</p>
                                        {featured.role && <p className="text-xs" style={{ color: s.textSub }}>{featured.role}</p>}
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* Mini preview grid */}
                        <div className="flex-1 grid grid-cols-2 gap-3 sm:gap-4">
                            {previewItems.map(({ item, idx }, pi) => (
                                <div key={pi} className={`rounded-xl border p-4 cursor-pointer transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md ${idx === featuredIdx ? 'ring-2' : ''}`}
                                    style={{
                                        borderColor: s.dark ? 'rgba(255,255,255,0.1)' : '#e5e7eb',
                                        backgroundColor: s.dark ? 'rgba(255,255,255,0.03)' : '#ffffff',
                                        ringColor: s.accent,
                                    }}
                                    onClick={() => selectFeatured(idx)}>
                                    <p className="text-xs leading-relaxed italic mb-3" style={{ color: s.textSub, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                        "{item.quote}"
                                    </p>
                                    {item.rating && <StarRating rating={item.rating} accent={s.accent} dark={s.dark} starColor={item.accent_color || config.star_color} />}
                                    <div className="flex items-center gap-2 mt-2 pt-2" style={{ borderTop: `1px solid ${s.dark ? 'rgba(255,255,255,0.06)' : '#f3f4f6'}` }}>
                                        <Avatar item={item} size="w-6 h-6" textSize="text-[9px]" />
                                        <div className="min-w-0">
                                            <p className="font-semibold text-[11px] truncate" style={{ color: s.text }}>{item.name}</p>
                                            {item.role && <p className="text-[10px] truncate" style={{ color: s.textSub }}>{item.role}</p>}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    // -- Spotlight variant (default) --
    return (
        <section className={`${s.padClass} relative`} style={s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle }}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <SectionBgImage config={config} mediaFolders={mediaFolders} />
            <div ref={ref} className={`relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                {content.title && <SectionHeader tag={content.tag || 'Opiniones'} title={content.title} subtitle={content.subtitle} config={config} s={s} />}

                <div className="relative min-h-[200px]">
                    {resolvedItems.map((item, i) => (
                        <div key={i} className={`transition-all duration-500 ${i === current ? 'opacity-100' : 'opacity-0 absolute inset-0'}`}>
                            <div className="text-center max-w-2xl mx-auto">
                                <svg className="w-10 h-10 mx-auto mb-6 opacity-20" style={{ color: s.accent }} fill="currentColor" viewBox="0 0 24 24">
                                    <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"/>
                                </svg>
                                <p className="text-lg md:text-xl leading-relaxed italic mb-6" style={{ color: s.textSub }}>"{item.quote}"</p>
                                {item.rating && <div className="flex justify-center mb-4"><StarRating rating={item.rating} accent={s.accent} dark={s.dark} starColor={item.accent_color || config.star_color} /></div>}
                                <div>
                                    <div className="mx-auto mb-3">
                                        <Avatar item={item} size="w-12 h-12 mx-auto" textSize="text-lg" />
                                    </div>
                                    <p className="font-semibold" style={{ color: s.text }}>{item.name}</p>
                                    {item.role && <p className="text-sm" style={{ color: s.textSub }}>{item.role}</p>}
                                    {item.badge && <span className="inline-block mt-1 text-[10px] font-semibold px-2.5 py-0.5 rounded-full text-white" style={{backgroundColor: s.accent}}>{item.badge}</span>}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {resolvedItems.length > 1 && (
                    <div className="flex justify-center gap-2 mt-8">
                        {resolvedItems.map((_, i) => (
                            <button key={i} onClick={() => setCurrent(i)}
                                className={`h-2 rounded-full transition-all duration-300 ${i === current ? 'w-8' : 'w-2'}`}
                                style={{ backgroundColor: i === current ? s.accent : hexToRgba(s.accent, 0.19) }} />
                        ))}
                    </div>
                )}
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
